import Template, { TemplateType } from "@/types/Template.interface"

export interface TemplateDefaultParams {
    type: TemplateType
}

interface State {
    templates: Template[]
    templateCount: number
}

const defaults = () => {
    return {
        templates: [],
        templateCount: 0
    }
}

export default {
    state: defaults(),
    mutations: {
        templateCountSet (state: State, count: number): void {
            state.templateCount = count
        },
        templatesPush (state: State, templates: Template[]): void {
            state.templates = window.removeDuplicatesById(state.templates, templates)
        },
        templatesRemove (state: State, ids: number[]): void {
            //  Get our current list of templates
            const templates = state.templates

            //  If no templates, do nothing
            if (!templates) return

            //  Else, filter out the removed ids
            const filtered = templates.filter((x: Template) => !ids.includes(x.id))

            //  Replace our list with the filtered list
            state.templates = filtered
        },
        templatesReset (state: State): void {
            Object.assign(state, defaults())
        },
        templatesSet (state: State, templates: Template[]): void {
            state.templates = templates
        }
    },
    actions: {
        templatesCount ({ commit, getters }): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates/count`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our templates count in the store
                commit('templateCountSet', response.data)

                //  Return the total templates count
                return response.data
            }).catch(() => null)
        },
        templatesDelete ({ commit, getters }, templateId: number): Promise<Template> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates/${templateId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Update our templates
                commit('templatesRemove', [response.data.id])

                //  Update our templates view
                commit('templatesViewTemplatesRemove', [response.data.id])

                //  Remove it from our message templates
                commit('messageTemplatesRemove', [response.data.id])

                //  REturn the deleted template
                return response.data
            }).catch(() => null)
        },
        templatesFind ({ commit, getters }, templateId: number): Promise<Template> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates/${templateId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our found template
                return response.data
            }).catch(() => null)
        },
        templatesGet ({ commit, getters }): Promise<Template[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our global templates list
                commit('templatesSet', response.data)

                //  Set our templatesView templates
                commit('templatesViewTemplatesSet', response.data)

                //  Set our message templates templates
                commit('messageTemplatesSet', response.data)

                //  Return our templates
                return response.data
            }).catch(() => null)
        },
        templatesGetDefault ({ commit, getters }, params: TemplateDefaultParams): Promise<Template> {
            //  Aseemble the API endpoint
            const url = `${getters.api}/message-defaults?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our default template
                commit('defaultTemplateSet', response.data)

                //  Return our found template
                return response.data
            }).catch(() => null)
        },
        templatesPost ({ commit, getters }, template: Template): Promise<Template> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates`

            //  Send the request
            return window.axios.post(url, template).then(response => {
                //  Update our global list
                commit('templatesPush', [response.data])

                //  Update our templates view list
                commit('templatesViewTemplatesPush', [response.data])

                //  Update our message templates
                commit('messageTemplatesPush', [response.data])

                //  REturn the new template
                return response.data
            }).catch(() => null)
        },
        templatesPut ({ commit, getters }, template: Template): Promise<Template> {
            //  Assemble the API endpoint
            const url = `${getters.api}/message-templates`

            //  Send the request
            return window.axios.put(url, template).then(response => {
                //  Update our global list
                commit('templatesPush', [response.data])

                //  Update our templates view list
                commit('templatesViewTemplatesPush', [response.data])

                //  Update our message templates
                commit('messageTemplatesPush', [response.data])

                //  Return the updated template
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        templateCount (state: State): number {
            return state.templateCount
        },
        templates (state: State): Template[] {
            return state.templates
        },
        templateBodyValidation: () => (template: Template): string => {
            //  If the template does NOT exist, return 'required' only
            if (!template) return 'required'

            //  Else, check if the template is an opt-in/out template
            const isOptTemplate = template.template_id === 1 || template.template_id === 2

            //  If the template is an 'opt-in/out' template, the max characters are 160
            if (isOptTemplate) return 'required|max:160'

            //  Else, the max characters are 255
            return 'required|max:960'
        }
    }
}
